import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MarkdownModule } from 'ngx-markdown';
import { staticAssetPrefix } from 'src/app/directives/static-src.directive';
import {
  DocumentationService,
  VersionDoc,
  VersionDocumentation,
} from 'src/app/services/documentation.service';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { DOCS_MANIFEST, DocEntry } from './docs-manifest';

/**
 * Documentation page (mockup A): left rail lists Guides (markdown — the
 * selected one renders in the reading pane) and Documents (downloadable
 * files), grouped by scope — Platform (static, ships with the app) and one
 * group per solution version the user has access to.
 */
@Component({
  selector: 'app-documentation',
  standalone: true,
  imports: [CommonModule, MarkdownModule],
  template: `
    <div class="doc-page">
      <div class="doc-hero">
        <span class="ic">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
               stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H20v16H5.5A1.5 1.5 0 0 0 4 20.5z" />
            <path d="M4 20.5A1.5 1.5 0 0 1 5.5 19H20" />
          </svg>
        </span>
        <div>
          <h1>Documentation</h1>
          <p>User guides &amp; documents</p>
        </div>
      </div>

      <div class="doc-layout">
        <aside>
          <!-- Guides -->
          <div class="doc-toc">
            <div class="h">Guides</div>

            <ng-container *ngIf="platformGuides.length">
              <div class="grp">Platform</div>
              <a
                *ngFor="let guide of platformGuides"
                [class.on]="isPlatformGuideSelected(guide)"
                (click)="selectPlatformGuide(guide)"
                >{{ guide.title }}</a
              >
            </ng-container>

            <ng-container *ngFor="let entry of versionsWithGuides">
              <div class="grp">
                {{ entry.solution_name }}
                <span class="vtag">{{ entry.version_name }}</span>
              </div>
              <a
                *ngFor="let guide of entry.guides"
                [class.on]="selectedVersionGuide?.id === guide.id"
                (click)="selectVersionGuide(guide)"
                >{{ guide.title }}</a
              >
            </ng-container>
          </div>

          <!-- Documents -->
          <div class="doc-toc" *ngIf="hasDocuments">
            <div class="h">Documents</div>

            <ng-container *ngIf="platformDocuments.length">
              <div class="grp">Platform</div>
              <a
                *ngFor="let doc of platformDocuments"
                [href]="prefix + doc.file"
                [attr.download]="fileNameOf(doc.file)"
              >
                <span class="file-l">
                  <span class="pdfico">PDF</span>
                  <span>{{ doc.title }}</span>
                </span>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                     stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 3v12M7 10l5 5 5-5" />
                  <path d="M5 21h14" />
                </svg>
              </a>
            </ng-container>

            <ng-container *ngFor="let entry of versionsWithDocuments">
              <div class="grp">
                {{ entry.solution_name }}
                <span class="vtag">{{ entry.version_name }}</span>
              </div>
              <a *ngFor="let doc of entry.documents" (click)="download(doc)">
                <span class="file-l">
                  <span class="pdfico" [ngClass]="iconClass(doc)">{{ iconLabel(doc) }}</span>
                  <span
                    >{{ doc.title
                    }}<span class="sub" *ngIf="doc.size">{{ formatSize(doc.size) }}</span></span
                  >
                </span>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                     stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 3v12M7 10l5 5 5-5" />
                  <path d="M5 21h14" />
                </svg>
              </a>
            </ng-container>
          </div>
        </aside>

        <!-- reading pane -->
        <div class="card">
          <div class="doc-body">
            <div class="doc-state" *ngIf="loadingContent">Loading…</div>
            <div class="doc-state err" *ngIf="contentError">{{ contentError }}</div>
            <ng-container *ngIf="!loadingContent && !contentError">
              <markdown *ngIf="markdownSrc" [src]="markdownSrc"></markdown>
              <markdown *ngIf="content !== undefined" [data]="content"></markdown>
              <div
                class="doc-state"
                *ngIf="!markdownSrc && content === undefined"
              >
                Select a guide on the left to start reading.
              </div>
            </ng-container>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./documentation.component.scss'],
})
export class DocumentationComponent implements OnInit {
  readonly prefix = staticAssetPrefix();

  platformGuides: DocEntry[] = DOCS_MANIFEST.filter(
    (entry) => entry.type === 'markdown'
  );
  platformDocuments: DocEntry[] = DOCS_MANIFEST.filter(
    (entry) => entry.type === 'pdf'
  );

  versions: VersionDocumentation[] = [];

  selectedPlatformGuide?: DocEntry;
  selectedVersionGuide?: VersionDoc;

  markdownSrc?: string;
  content?: string;
  loadingContent = false;
  contentError = '';

  constructor(
    private docService: DocumentationService,
    private snackBar: SharedFeaturesService
  ) {}

  ngOnInit(): void {
    if (this.platformGuides.length) {
      this.selectPlatformGuide(this.platformGuides[0]);
    }

    this.docService.getMyDocumentation().subscribe({
      next: (response: any) => {
        this.versions = response?.msg || [];
      },
      error: async (error) => {
        this.snackBar.openSnackBar(
          await this.docService.parseError(
            error,
            'Unable to load solution documentation'
          ),
          'Close',
          'error-snackbar'
        );
      },
    });
  }

  get versionsWithGuides(): VersionDocumentation[] {
    return this.versions.filter((entry) => entry.guides?.length);
  }

  get versionsWithDocuments(): VersionDocumentation[] {
    return this.versions.filter((entry) => entry.documents?.length);
  }

  get hasDocuments(): boolean {
    return !!this.platformDocuments.length || !!this.versionsWithDocuments.length;
  }

  isPlatformGuideSelected(guide: DocEntry): boolean {
    return this.selectedPlatformGuide?.id === guide.id;
  }

  selectPlatformGuide(guide: DocEntry): void {
    this.selectedPlatformGuide = guide;
    this.selectedVersionGuide = undefined;
    this.content = undefined;
    this.contentError = '';
    this.loadingContent = false;
    this.markdownSrc = this.prefix + guide.file;
  }

  selectVersionGuide(guide: VersionDoc): void {
    this.selectedPlatformGuide = undefined;
    this.selectedVersionGuide = guide;
    this.markdownSrc = undefined;
    this.content = undefined;
    this.contentError = '';

    if (!guide.view_url) {
      this.contentError = 'This guide has no content yet.';
      return;
    }

    this.loadingContent = true;
    this.docService.getDoc(guide.view_url).subscribe({
      next: (response: any) => {
        // Only apply if the user hasn't clicked away meanwhile.
        if (this.selectedVersionGuide?.id !== guide.id) return;
        const doc = response?.msg ?? response;
        this.content = doc?.content || '';
        this.loadingContent = false;
      },
      error: async (error) => {
        if (this.selectedVersionGuide?.id !== guide.id) return;
        this.loadingContent = false;
        this.contentError = await this.docService.parseError(
          error,
          'Unable to load this guide'
        );
      },
    });
  }

  download(doc: VersionDoc): void {
    if (!doc.url) return;
    this.docService
      .downloadDoc(doc.url, doc.file_name || doc.title)
      .subscribe({
        error: async (error) => {
          this.snackBar.openSnackBar(
            await this.docService.parseError(error, 'Download failed'),
            'Close',
            'error-snackbar'
          );
        },
      });
  }

  fileNameOf(path: string): string {
    return path.split('/').pop() || path;
  }

  iconLabel(doc: VersionDoc): string {
    const ext = (doc.file_name || '').split('.').pop()?.toLowerCase();
    if (ext === 'xlsx') return 'XLSX';
    if (ext === 'docx') return 'DOCX';
    return 'PDF';
  }

  iconClass(doc: VersionDoc): string {
    const ext = (doc.file_name || '').split('.').pop()?.toLowerCase();
    if (ext === 'xlsx') return 'xls';
    if (ext === 'docx') return 'docx';
    return '';
  }

  formatSize(size: number | null | undefined): string {
    if (!size) return '';
    if (size >= 1024 * 1024) return (size / (1024 * 1024)).toFixed(1) + ' MB';
    if (size >= 1024) return Math.round(size / 1024) + ' KB';
    return size + ' B';
  }
}
